export type BottomSheetState = "collapsed" | "half" | "expanded";

export const BOTTOM_SHEET_STATES: readonly BottomSheetState[] = ["collapsed", "half", "expanded"] as const;

const DRAG_THRESHOLD_PX = 40;

const TRANSFORM_BASE: Record<BottomSheetState, string> = {
  collapsed: "calc(100% - var(--collapsed-sheet-height))",
  half: "42dvh",
  expanded: "0px",
};

export function nextStateForDrag(current: BottomSheetState, deltaY: number): BottomSheetState {
  if (Math.abs(deltaY) < DRAG_THRESHOLD_PX) {
    return current;
  }
  const index = BOTTOM_SHEET_STATES.indexOf(current);
  const next = deltaY < 0 ? index + 1 : index - 1;
  return BOTTOM_SHEET_STATES[Math.max(0, Math.min(BOTTOM_SHEET_STATES.length - 1, next))] ?? current;
}

function cycleState(current: BottomSheetState): BottomSheetState {
  const index = BOTTOM_SHEET_STATES.indexOf(current);
  return BOTTOM_SHEET_STATES[(index + 1) % BOTTOM_SHEET_STATES.length] ?? current;
}

function stepState(current: BottomSheetState, delta: 1 | -1): BottomSheetState {
  const index = BOTTOM_SHEET_STATES.indexOf(current);
  const next = Math.max(0, Math.min(BOTTOM_SHEET_STATES.length - 1, index + delta));
  return BOTTOM_SHEET_STATES[next] ?? current;
}

function grabberLabel(state: BottomSheetState): string {
  return `Panel size: ${state}. Use arrow keys to resize.`;
}

function readState(sheet: HTMLElement): BottomSheetState {
  return BOTTOM_SHEET_STATES.includes(sheet.dataset.state as BottomSheetState)
    ? (sheet.dataset.state as BottomSheetState)
    : "collapsed";
}

function isDesktopLayout(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(min-width: 768px)").matches;
}

export function setBottomSheetState(sheet: HTMLElement, state: BottomSheetState): void {
  sheet.dataset.state = state;
  sheet.setAttribute("aria-expanded", state === "collapsed" ? "false" : "true");
  const shell = sheet.closest<HTMLElement>("[data-app-shell]");
  if (shell) {
    shell.dataset.sheetState = state;
  }
  const grabber = sheet.querySelector<HTMLButtonElement>("[data-sheet-grabber]");
  grabber?.setAttribute("aria-label", grabberLabel(state));
}

export function createBottomSheetController(sheet: HTMLElement): { set(state: BottomSheetState): void } {
  const grabber = sheet.querySelector<HTMLButtonElement>("[data-sheet-grabber]");
  const summaryToggle = sheet.querySelector<HTMLButtonElement>("[data-sheet-summary-toggle]");
  const initial = readState(sheet);
  setBottomSheetState(sheet, initial);

  let dragPointerId: number | null = null;
  let startY = 0;
  let startState: BottomSheetState = initial;
  let lastDeltaY = 0;
  let ignoreClick = false;

  function onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || isDesktopLayout()) {
      return;
    }
    dragPointerId = event.pointerId;
    startY = event.clientY;
    startState = readState(sheet);
    lastDeltaY = 0;
    ignoreClick = false;
    try {
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      // jsdom and some browsers may not implement capture.
    }
  }

  function onPointerMove(event: PointerEvent): void {
    if (dragPointerId !== event.pointerId) {
      return;
    }
    lastDeltaY = event.clientY - startY;
    sheet.classList.add("is-dragging");
    sheet.style.transform = `translateY(calc(${TRANSFORM_BASE[startState]} + ${lastDeltaY}px))`;
  }

  function onPointerUp(event: PointerEvent): void {
    if (dragPointerId !== event.pointerId) {
      return;
    }
    dragPointerId = null;
    sheet.classList.remove("is-dragging");
    sheet.style.removeProperty("transform");
    if (Math.abs(lastDeltaY) >= DRAG_THRESHOLD_PX) {
      ignoreClick = true;
    }
    setBottomSheetState(sheet, nextStateForDrag(startState, lastDeltaY));
  }

  function bindDrag(element: HTMLElement): void {
    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointermove", onPointerMove);
    element.addEventListener("pointerup", onPointerUp);
    element.addEventListener("pointercancel", onPointerUp);
  }

  grabber?.addEventListener("keydown", (event) => {
    const current = readState(sheet);
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setBottomSheetState(sheet, cycleState(current));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setBottomSheetState(sheet, stepState(current, 1));
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setBottomSheetState(sheet, stepState(current, -1));
    }
  });

  if (grabber) {
    bindDrag(grabber);
  }
  if (summaryToggle) {
    bindDrag(summaryToggle);
    summaryToggle.addEventListener("click", (event) => {
      if (ignoreClick) {
        event.preventDefault();
        ignoreClick = false;
        return;
      }
      setBottomSheetState(sheet, "half");
    });
  }

  return {
    set(state: BottomSheetState) {
      setBottomSheetState(sheet, state);
    },
  };
}
